import CryptoJS from 'crypto-js';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  DEFAULT_CHIP_COLOR,
  PRICING_MODEL_TYPES,
  PRODUCT_LINE_CHIP_COLORS,
  ROLES,
  SECRET_KEY,
} from '../constants/AppConstants';

dayjs.extend(utc);
dayjs.extend(timezone);

// makes dayjs handle DST automatically: CET (UTC+1) in winter, CEST (UTC+2) in summer.
const EUROPEAN_TIMEZONE = 'Europe/Paris';

/**
 * Formats a UTC/ISO timestamp in European time (CET/CEST), DST-aware.
 * Returns an object with the formatted time and the correct abbreviation,
 * so callers don't hardcode the offset or the label.
 */
export const formatInEuropeanTime = (
  dateTime,
  formatStr = 'DD/MM/YY HH:mm',
) => {
  if (!dateTime) {
    return { time: '', label: '' };
  }
  const d = dayjs.utc(dateTime).tz(EUROPEAN_TIMEZONE);
  if (!d.isValid()) {
    return { time: '', label: '' };
  }
  const label = d.utcOffset() === 120 ? 'CEST' : 'CET';
  return { time: d.format(formatStr), label };
};

export const decryptName = (encryptedName) => {
  const bytes = CryptoJS.AES.decrypt(encryptedName, SECRET_KEY);
  return bytes?.toString(CryptoJS.enc.Utf8);
};

export const encryptName = (name) => {
  return CryptoJS.AES.encrypt(name, SECRET_KEY).toString();
};

export const convertDateToNoteFormat = (isoDateString) => {
  if (!isoDateString) {
    return '';
  }
  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const convertDateToUIFormat = (date) => {
  if (date && typeof date === 'string') {
    const [year, month, day] = date.split('T')[0].split('-');
    if (year && month && day) {
      return `${day}-${month}-${year}`;
    }
  }
  return '';
};

export const convertDateToDateAndTimeFormat = (dateString, options) => {
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  if (options?.classic) return `${day}/${month}/${year}, ${hours}:${minutes}`;

  if (options?.narrow) return `${day}.${month}.${year} ${hours}:${minutes}`;

  return `${day}-${month}-${year} (${hours}:${minutes})`;
};

export const convertDateToTimelineFormat = (dateString) => {
  const date = new Date(dateString);
  const formattedDate = date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return formattedDate;
};

export const convertDateToISO = (date) => {
  if (date && typeof date === 'string') {
    const [day, month, year] = date.split('-');
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
  }
  return '';
};

export const todayISO = () => new Date().toISOString().substring(0, 10);

export const getPricingModelCodeByValue = (value) => {
  const pricingModel = PRICING_MODEL_TYPES.find(
    (model) => model.value === value,
  );
  return pricingModel ? pricingModel.code : null;
};

export const getRoleCodeByValue = (value) => {
  const role = ROLES.find((role) => role.value === value);
  return role ? role.code : null;
};

export const getValueByCode = (value) => {
  const role = ROLES.find((role) => role.code === value);
  return role ? role.value : null;
};

export const verifyRole = (role) => {
  let res = '';
  switch (role) {
    case 'Contact for invoices':
      res = 'invoicing';
      break;
    case 'Payroll officer':
      res = 'payout';
      break;
    case 'Other':
      res = 'other';
      break;
    case 'HR manager':
      res = 'HR';
      break;
    default:
      res = role;
      break;
  }
  return res;
};

export const getProductLineChipColor = (productLine) => {
  return PRODUCT_LINE_CHIP_COLORS[productLine] || DEFAULT_CHIP_COLOR;
};

export const getWalletUrl = (wallet) => {
  const encryptedName = encryptName(`Wallet ID ${wallet.id}`);
  const encodedEncryptedName = encodeURIComponent(encryptedName);
  return `/wallets/${encodedEncryptedName}?id=${wallet.id}`;
};

export const getChipConfig = (inpObj) => {
  if (
    inpObj?.isMedicallyNecessaryRequested &&
    !inpObj?.isMedicallyNecessaryConfirmed
  ) {
    const days = inpObj?.medicallyNecessaryProofDueInDays;
    return {
      label: days > 0 ? `${days} days left` : `${days} days overdue`,
      color: days > 0 ? '#E7EDF9' : '#FEA7A7',
    };
  }
  if (
    inpObj?.isMedicallyNecessaryRequested &&
    inpObj?.isMedicallyNecessaryConfirmed
  ) {
    return {
      label: 'Done',
      color: '#DFFCEB',
    };
  }
  return {
    label: 'NA',
    color: '#F3F6FB',
  };
};
